import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";

const TelemetryContext = createContext(null);

const STORAGE_KEY = "educa.session_id.v1";
const ENDPOINT = "/api/telemetry";

function getSessionId() {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = window.localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    return "anon-" + Math.random().toString(36).slice(2, 10);
  }
}

export function TelemetryProvider({ children }) {
  const queue = useRef([]);
  const flushing = useRef(false);
  const sessionId = useMemo(() => getSessionId(), []);

  const flush = useCallback(async () => {
    if (flushing.current || queue.current.length === 0) return;
    flushing.current = true;
    const batch = queue.current.splice(0, queue.current.length);
    try {
      await fetch(ENDPOINT, {
        method: "POST",
        keepalive: true,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, events: batch }),
      });
    } catch (err) {
      // Queue back; ignore in dev
      queue.current.unshift(...batch);
    } finally {
      flushing.current = false;
    }
  }, [sessionId]);

  useEffect(() => {
    const t = window.setInterval(flush, 5000);
    const onBeforeUnload = () => flush();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.clearInterval(t);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [flush]);

  const emit = useCallback(
    (name, payload = {}) => {
      const ev = {
        name,
        payload,
        ts: Date.now(),
        path: typeof window !== "undefined" ? window.location.pathname : "ssr",
      };
      queue.current.push(ev);
      if (queue.current.length >= 10) flush();
    },
    [flush],
  );

  const value = useMemo(() => ({ emit, sessionId, flush }), [emit, sessionId, flush]);

  return <TelemetryContext.Provider value={value}>{children}</TelemetryContext.Provider>;
}

export function useTelemetry() {
  const ctx = useContext(TelemetryContext);
  if (!ctx) throw new Error("useTelemetry must be used inside <TelemetryProvider>");
  return ctx;
}
